import React, { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Activity,
  Bot,
  PauseCircle,
  PlayCircle,
  Power,
  RadioTower,
  ShieldCheck,
  TrendingUp,
} from "lucide-react";
import DashboardLayout from "../components/dashboard/DashboardLayout";
import { Card } from "../components/ui/Card";
import { useTrading } from "../context/TradingContext";
import { calculateRSI, intervalToSeconds } from "../services/trading/indicators";
import {
  createMarketStream,
  normalizeMarketInterval,
  toNormalizedCandle,
} from "../services/trading/binanceStream";
import {
  BOT_PROFILES,
  MARKET_OPTIONS,
  getMarketBySymbol,
} from "../services/trading/marketCatalog";
import {
  buildPassiveActivity,
  createExecutionLog,
  createInitialExecutionRuntime,
  getBotProfile,
  getStateMeta,
  runTradeCycle,
  syncRuntimeWithMarket,
} from "../services/trading/liveExecutionEngine";

const TRADE_LIVE_STORAGE_KEY = "gainwise-trade-live-state-v2";

const EXECUTION_FALLBACK_SETTINGS = {
  botSelection: "lowRisk",
  pair: "BTCUSDT",
  tradeAmount: 75,
  cycleDuration: "3s",
  mode: "paper",
  profitSpeed: 5,
  volatility: 5,
  maxRoi: 300,
  tradeFrequency: 5,
};

const DEFAULT_SETTINGS = {
  ...EXECUTION_FALLBACK_SETTINGS,
  botSelection: "",
  pair: "",
  tradeAmount: null,
};

const getDefaultSettings = () => ({ ...DEFAULT_SETTINGS });

const getExecutionSettings = (settings) => ({
  ...EXECUTION_FALLBACK_SETTINGS,
  ...settings,
  botSelection: settings.botSelection || EXECUTION_FALLBACK_SETTINGS.botSelection,
  pair: settings.pair || EXECUTION_FALLBACK_SETTINGS.pair,
  tradeAmount:
    Number.isFinite(Number(settings.tradeAmount)) && Number(settings.tradeAmount) > 0
      ? Number(settings.tradeAmount)
      : EXECUTION_FALLBACK_SETTINGS.tradeAmount,
});

const normalizeCandles = (candles) => {
  const byTime = new Map();
  candles.forEach((candle) => byTime.set(candle.time, candle));
  return [...byTime.values()].sort((left, right) => left.time - right.time).slice(-240);
};

const seedCandles = (pair, interval) => {
  const market = getMarketBySymbol(pair);
  const candles = [];
  const stepSeconds = intervalToSeconds(normalizeMarketInterval(interval));
  const nowSeconds = Math.floor(Date.now() / 1000);
  const alignedNow = nowSeconds - (nowSeconds % stepSeconds);
  let price = market.seedPrice;

  for (let index = 140; index >= 0; index -= 1) {
    const time = alignedNow - index * stepSeconds;
    const drift = (Math.random() - 0.5) * market.volatility;
    const open = price;
    const close = Math.max(0.0001, open + drift);
    candles.push({
      time,
      open,
      high: Math.max(open, close) + Math.random() * market.volatility * 0.35,
      low: Math.min(open, close) - Math.random() * market.volatility * 0.35,
      close,
      volume: 100 + Math.random() * 1200,
    });
    price = close;
  }

  return normalizeCandles(candles);
};

const formatMoney = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(value || 0);

const formatPct = (value) => `${value >= 0 ? "+" : ""}${value.toFixed(2)}%`;

const getDraftConfiguration = (settings, tradeAmountInput) => ({
  botSelection: settings.botSelection || "",
  pair: settings.pair || "",
  tradeAmount:
    tradeAmountInput === "" ? Number.NaN : Number.parseFloat(tradeAmountInput),
});

const isConfigurationComplete = (configuration) =>
  Boolean(
    configuration.botSelection &&
      configuration.pair &&
      Number.isFinite(configuration.tradeAmount) &&
      configuration.tradeAmount > 0
  );

const areConfigurationsEqual = (left, right) =>
  Boolean(left) &&
  Boolean(right) &&
  left.botSelection === right.botSelection &&
  left.pair === right.pair &&
  Number(left.tradeAmount) === Number(right.tradeAmount);

const loadSavedTradeLiveState = () => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const savedState = window.localStorage.getItem(TRADE_LIVE_STORAGE_KEY);
    return savedState ? JSON.parse(savedState) : null;
  } catch (error) {
    return null;
  }
};

const getSharedPortfolioBalance = (account) =>
  Number(
    Math.max(
      0,
      Number(account?.currentBalance ?? 0),
      Number(account?.equity ?? 0),
      Number(account?.availableBalance ?? 0)
    ).toFixed(2)
  );

const TradeLive = () => {
  const { account, addNotification, addTransaction, syncAccountState } = useTrading();
  const savedTradeLiveState = useMemo(() => loadSavedTradeLiveState(), []);
  const hasRestorableSession =
    Boolean(savedTradeLiveState?.runtime?.state) &&
    savedTradeLiveState.runtime.state !== "stopped";
  const [settings, setSettings] = useState(
    hasRestorableSession && savedTradeLiveState?.settings
      ? { ...DEFAULT_SETTINGS, ...savedTradeLiveState.settings }
      : getDefaultSettings()
  );
  const [tradeAmountInput, setTradeAmountInput] = useState(() =>
    hasRestorableSession && Number.isFinite(savedTradeLiveState?.settings?.tradeAmount)
      ? String(savedTradeLiveState.settings.tradeAmount)
      : ""
  );
  const [savedConfiguration, setSavedConfiguration] = useState(
    hasRestorableSession ? savedTradeLiveState?.savedConfiguration || null : null
  );
  const [isConfigurationSaved, setIsConfigurationSaved] = useState(() =>
    areConfigurationsEqual(
      hasRestorableSession ? savedTradeLiveState?.savedConfiguration : null,
      getDraftConfiguration(
        hasRestorableSession && savedTradeLiveState?.settings
          ? { ...DEFAULT_SETTINGS, ...savedTradeLiveState.settings }
          : getDefaultSettings(),
        hasRestorableSession && Number.isFinite(savedTradeLiveState?.settings?.tradeAmount)
          ? String(savedTradeLiveState.settings.tradeAmount)
          : ""
      )
    )
  );
  const effectiveSettings = useMemo(
    () => getExecutionSettings(settings),
    [settings]
  );
  const [candles, setCandles] = useState(() =>
    seedCandles(
      (hasRestorableSession && savedTradeLiveState?.settings?.pair) ||
        EXECUTION_FALLBACK_SETTINGS.pair,
      (hasRestorableSession && savedTradeLiveState?.settings?.cycleDuration) ||
        EXECUTION_FALLBACK_SETTINGS.cycleDuration
    )
  );
  const [runtime, setRuntime] = useState(() => {
    const baseRuntime = createInitialExecutionRuntime(
      hasRestorableSession && savedTradeLiveState?.settings
        ? getExecutionSettings({ ...DEFAULT_SETTINGS, ...savedTradeLiveState.settings })
        : getExecutionSettings(getDefaultSettings())
    );

    if (!savedTradeLiveState?.runtime) {
      return baseRuntime;
    }

    const savedRuntime = savedTradeLiveState.runtime;
    if (savedRuntime.state === "stopped") {
      return {
        ...baseRuntime,
        startingBalance: savedRuntime.currentBalance ?? baseRuntime.currentBalance,
        currentBalance: savedRuntime.currentBalance ?? baseRuntime.currentBalance,
        equity: savedRuntime.currentBalance ?? baseRuntime.currentBalance,
      };
    }

    return {
      ...baseRuntime,
      ...savedRuntime,
      state:
        savedRuntime.state && savedRuntime.state !== "stopped"
          ? "paused"
          : savedRuntime.state || "stopped",
      nextTradeAt: null,
      pausedRemainingMs: null,
      logs: [
        ...(savedRuntime.logs || []),
        createExecutionLog(
          "Previous AI execution session restored after refresh.",
          "info"
        ),
      ].slice(-140),
    };
  });
  const [transportStatus, setTransportStatus] = useState("idle");
  const runtimeRef = useRef(runtime);
  const candlesRef = useRef(candles);
  const logsRef = useRef(null);
  const streamRef = useRef(null);
  const sharedPortfolioBalance = getSharedPortfolioBalance(account);
  const draftConfiguration = useMemo(
    () => getDraftConfiguration(settings, tradeAmountInput),
    [settings, tradeAmountInput]
  );
  const configurationReady =
    isConfigurationSaved && isConfigurationComplete(draftConfiguration);
  const configurationChecklist = [
    {
      label: "Bot Type",
      value: BOT_PROFILES[draftConfiguration.botSelection]?.label || "Not selected",
      complete: Boolean(draftConfiguration.botSelection),
    },
    {
      label: "Trading Pair",
      value: draftConfiguration.pair
        ? getMarketBySymbol(draftConfiguration.pair).label
        : "Not selected",
      complete: Boolean(draftConfiguration.pair),
    },
    {
      label: "Trade Amount",
      value:
        Number.isFinite(draftConfiguration.tradeAmount) && draftConfiguration.tradeAmount > 0
          ? formatMoney(draftConfiguration.tradeAmount)
          : "Enter amount",
      complete:
        Number.isFinite(draftConfiguration.tradeAmount) && draftConfiguration.tradeAmount > 0,
    },
  ];
  const configurationCompletion = Math.round(
    (configurationChecklist.filter((item) => item.complete).length /
      configurationChecklist.length) *
      100
  );

  useEffect(() => {
    runtimeRef.current = runtime;
  }, [runtime]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const persistedSettings =
      isConfigurationSaved || runtime.state !== "stopped"
        ? settings
        : getDefaultSettings();

    window.localStorage.setItem(
      TRADE_LIVE_STORAGE_KEY,
      JSON.stringify({
        settings: persistedSettings,
        runtime,
        savedConfiguration: isConfigurationSaved ? savedConfiguration : null,
        isConfigurationSaved: Boolean(isConfigurationSaved),
      })
    );
  }, [isConfigurationSaved, runtime, savedConfiguration, settings]);

  useEffect(() => {
    setTradeAmountInput(
      Number.isFinite(settings.tradeAmount) ? String(settings.tradeAmount) : ""
    );
  }, [settings.tradeAmount]);

  useEffect(() => {
    setIsConfigurationSaved(
      areConfigurationsEqual(savedConfiguration, draftConfiguration)
    );
  }, [draftConfiguration, savedConfiguration]);

  useEffect(() => {
    if (runtime.state === "stopped") {
      return;
    }

    syncAccountState({
      startingBalance: runtime.startingBalance,
      currentBalance: runtime.currentBalance,
      equity: runtime.equity,
      availableBalance: runtime.currentBalance,
      activeTradingAmount: runtime.activeTradingAmount,
      totalProfitLoss: runtime.totalProfitLoss,
      realizedProfitLoss: runtime.realizedProfitLoss,
      totalInvested: runtime.activeTradingAmount,
      sessionsCompleted: runtime.sessionsCompleted,
      wins: runtime.wins,
      losses: runtime.losses,
    });
  }, [runtime, syncAccountState]);

  useEffect(() => {
    if (runtime.state !== "stopped") {
      return;
    }

    setRuntime((previous) => {
      if (
        previous.state !== "stopped" ||
        previous.currentBalance === sharedPortfolioBalance ||
        !Number.isFinite(sharedPortfolioBalance)
      ) {
        return previous;
      }

      return {
        ...previous,
        startingBalance: sharedPortfolioBalance,
        currentBalance: sharedPortfolioBalance,
        equity: sharedPortfolioBalance,
      };
    });
  }, [runtime.state, sharedPortfolioBalance]);

  useEffect(() => {
    candlesRef.current = candles;
  }, [candles]);

  useEffect(() => {
    if (logsRef.current) {
      logsRef.current.scrollTop = logsRef.current.scrollHeight;
    }
  }, [runtime.logs]);

  const botProfile = useMemo(
    () => getBotProfile(effectiveSettings.botSelection),
    [effectiveSettings.botSelection]
  );
  const selectedMarket = useMemo(
    () => getMarketBySymbol(effectiveSettings.pair),
    [effectiveSettings.pair]
  );
  const latestCandle = candles[candles.length - 1];
  const marketPrice = latestCandle?.close || selectedMarket.seedPrice;
  const lastRsi = useMemo(() => calculateRSI(candles, 14), [candles]);
  const stateMeta = getStateMeta(runtime.state);
  const sessionIsActive =
    (runtime.simulationStartedAt !== null && runtime.state !== "stopped") ||
    (runtime.state === "stopped" &&
      (runtime.lastTradeStake > 0 ||
        runtime.lastTradeValue > 0 ||
        runtime.sessionsCompleted > 0 ||
        runtime.totalProfitLoss !== 0));
  const tradeStake =
    sessionIsActive ? runtime.sessionStake || effectiveSettings.tradeAmount : 0;
  const stakePlusPnlValue = sessionIsActive
    ? Math.max(0, tradeStake + runtime.totalProfitLoss)
    : 0;
  const lastClosedTradeTone =
    runtime.lastClosedTradePnl > 0
      ? "text-emerald-300"
      : runtime.lastClosedTradePnl < 0
      ? "text-rose-300"
      : "text-slate-100";
  const lastClosedTradeLabel = `${
    runtime.lastClosedTradePnl >= 0 ? "+" : "-"
  }$${Math.abs(runtime.lastClosedTradePnl || 0).toFixed(2)}`;

  const pushLogs = (entries) => {
    if (!entries?.length) {
      return;
    }

    setRuntime((previous) => ({
      ...previous,
      logs: [...previous.logs, ...entries].slice(-140),
    }));
  };

  const handleEngineEvents = (events) => {
    events.forEach((event) => {
      if (event.type === "transaction") {
        addTransaction(event.transaction);
      }
    });
  };

  useEffect(() => {
    streamRef.current?.close();
    setCandles(seedCandles(effectiveSettings.pair, effectiveSettings.cycleDuration));
    setRuntime((previous) => ({
      ...createInitialExecutionRuntime(effectiveSettings),
      ...previous,
      botSelection: effectiveSettings.botSelection,
      currentBotMode: effectiveSettings.mode,
      cycleDuration: effectiveSettings.cycleDuration,
      cycleMs: intervalToSeconds(effectiveSettings.cycleDuration) * 1000,
      sessionStake: previous.sessionStake || effectiveSettings.tradeAmount,
      logs:
        previous.logs?.length
          ? previous.logs
          : [
              createExecutionLog(
                `Loaded ${botProfile.label} AI execution on ${selectedMarket.label}. Awaiting operator input.`,
                "info"
              ),
            ],
    }));

    const stream = createMarketStream({
      symbol: effectiveSettings.pair,
      interval: effectiveSettings.cycleDuration,
      onStatus: ({ status }) => setTransportStatus(status),
      onActivity: (activity) => pushLogs([activity]),
      onTicker: (ticker) => {
        setCandles((previous) => {
          if (!previous.length) {
            return previous;
          }

          const next = [...previous];
          const last = next[next.length - 1];
          next[next.length - 1] = {
            ...last,
            close: ticker.lastPrice,
            high: Math.max(last.high, ticker.lastPrice),
            low: Math.min(last.low, ticker.lastPrice),
            volume: ticker.volume,
          };
          return normalizeCandles(next);
        });

        setRuntime((previous) => {
          if (previous.state === "paused" || previous.state === "stopped") {
            return previous;
          }

          return syncRuntimeWithMarket({
            runtime: previous,
            marketPrice: ticker.lastPrice,
          });
        });
      },
      onKline: (kline) => {
        setCandles((previous) => {
          const candle = toNormalizedCandle(kline);
          const next = [...previous];
          const existingIndex = next.findIndex((entry) => entry.time === candle.time);

          if (existingIndex === -1) {
            next.push(candle);
          } else {
            next[existingIndex] = candle;
          }

          return normalizeCandles(next);
        });
      },
      onError: () => {
        pushLogs([createExecutionLog("Market stream reported an error.", "error")]);
        setTransportStatus("error");
      },
    });

    streamRef.current = stream;

    return () => {
      stream.close();
    };
  }, [botProfile.label, effectiveSettings, selectedMarket.label]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      const currentCandles = candlesRef.current;
      const currentRuntime = runtimeRef.current;
      const latest = currentCandles[currentCandles.length - 1];

      if (!latest) {
        return;
      }

      const confidenceSeed =
        currentCandles.length > 1
          ? latest.close - currentCandles[currentCandles.length - 2].close
          : 0;
      const passive = buildPassiveActivity({
        runtime: currentRuntime,
        rsi: calculateRSI(currentCandles, 14),
        confidence: Math.min(95, botProfile.confidenceBias + Math.abs(confidenceSeed)),
        marketLabel: selectedMarket.label,
        marketPrice: latest.close,
      });

      if (passive.logs.length) {
        setRuntime((previous) => ({
          ...passive.runtime,
          logs: [...previous.logs, ...passive.logs].slice(-140),
        }));
      } else {
        setRuntime(passive.runtime);
      }

      const nextRuntime = runtimeRef.current;
      if (
        nextRuntime.state !== "paused" &&
        nextRuntime.state !== "stopped" &&
        nextRuntime.nextTradeAt &&
        Date.now() >= nextRuntime.nextTradeAt
      ) {
        const result = runTradeCycle({
          runtime: nextRuntime,
          settings: effectiveSettings,
          marketPrice: latest.close,
          rsi: calculateRSI(currentCandles, 14),
          candleTime: latest.time,
          priceDrift: confidenceSeed,
        });

        handleEngineEvents(result.events);
        setRuntime(
          syncRuntimeWithMarket({
            runtime: result.runtime,
            marketPrice: latest.close,
          })
        );
      }
    }, 3000);

    return () => clearInterval(timer);
  }, [botProfile.confidenceBias, effectiveSettings, selectedMarket.label]);

  const startBot = () => {
    if (!configurationReady) {
      addNotification({
        type: "error",
        title: "Trade Live",
        message: "Please save your bot configuration before starting.",
      });
      return;
    }

    setRuntime((previous) => {
      const shouldResetSession = previous.state === "stopped";
      const baseRuntime = shouldResetSession
        ? {
            ...createInitialExecutionRuntime(effectiveSettings),
            startingBalance: sharedPortfolioBalance,
            currentBalance: sharedPortfolioBalance,
            equity: sharedPortfolioBalance,
            sessionStake: effectiveSettings.tradeAmount,
          }
        : previous;
      const base = previous.state === "paused" && previous.pausedRemainingMs
        ? previous.pausedRemainingMs
        : intervalToSeconds(effectiveSettings.cycleDuration) * 1000;

      return {
        ...baseRuntime,
        state: "scanning",
        simulationStartedAt: shouldResetSession
          ? Date.now()
          : previous.simulationStartedAt || Date.now(),
        botSelection: effectiveSettings.botSelection,
        currentBotMode: effectiveSettings.mode,
        sessionStake: effectiveSettings.tradeAmount,
        cycleDuration: effectiveSettings.cycleDuration,
        cycleMs: intervalToSeconds(effectiveSettings.cycleDuration) * 1000,
        nextTradeAt: Date.now() + base,
        pausedRemainingMs: null,
        activeTradeStatus:
          baseRuntime.openPosition?.marketLabel || "Scanning market for next setup",
        logs: [
          ...baseRuntime.logs,
          createExecutionLog(`Initializing ${botProfile.label} smart execution...`, "success"),
          createExecutionLog("This site is an AI-powered virtual trading and strategy evalution enviroment.", "warn"),
          createExecutionLog(
            `Auto-cycle armed for ${selectedMarket.label}. Background execution runs every 3 seconds.`,
            "info"
          ),
        ].slice(-140),
      };
    });

    addNotification({
      type: "success",
      title: "Trade Live",
      message: `${botProfile.label} smart practice execution started on ${selectedMarket.label}.`,
    });
  };

  const saveConfiguration = () => {
    if (!isConfigurationComplete(draftConfiguration)) {
      addNotification({
        type: "error",
        title: "Trade Live",
        message: "Please complete the bot type, trading pair, and trade amount before saving.",
      });
      return;
    }

    const nextConfiguration = {
      botSelection: draftConfiguration.botSelection,
      pair: draftConfiguration.pair,
      tradeAmount: draftConfiguration.tradeAmount,
    };
    setSettings((previous) => ({
      ...previous,
      ...nextConfiguration,
    }));
    setSavedConfiguration(nextConfiguration);
    setIsConfigurationSaved(true);
    addNotification({
      type: "success",
      title: "Trade Live",
      message: "Configuration saved successfully",
    });
  };

  const pauseBot = () => {
    if (runtimeRef.current.state === "paused") {
      startBot();
      return;
    }

    if (runtimeRef.current.state === "stopped") {
      return;
    }

    setRuntime((previous) => ({
      ...previous,
      state: "paused",
      pausedRemainingMs: previous.nextTradeAt
        ? Math.max(1000, previous.nextTradeAt - Date.now())
        : previous.cycleMs,
      nextTradeAt: null,
      activeTradeStatus: previous.openPosition
        ? "Bot paused with active position preserved"
        : "Bot paused by operator",
      logs: [
        ...previous.logs,
        createExecutionLog(
          previous.openPosition
            ? "Bot paused. Active execution event preserved for resume."
            : "Bot paused. Live stream remains connected.",
          "warn"
        ),
      ].slice(-140),
    }));
  };

  const stopBot = () => {
    setRuntime((previous) => {
      if (!previous.openPosition) {
        const resetBalance = previous.currentBalance;
        return {
          ...previous,
          simulationStartedAt: null,
          startingBalance: resetBalance,
          sessionStake: effectiveSettings.tradeAmount,
          currentBalance: resetBalance,
          equity: resetBalance,
          state: "stopped",
          nextTradeAt: null,
          pausedRemainingMs: null,
          activeTradingAmount: 0,
          activeTradeStatus: "Trading stopped",
          logs: [
            ...previous.logs,
            createExecutionLog(
              "Bot stopped. Session ended and values preserved until restart or refresh.",
              "error"
            ),
          ].slice(-140),
        };
      }

      const marketValue = previous.openPosition.quantity * marketPrice;
      const realizedPnL = Number((marketValue - previous.openPosition.invested).toFixed(2));
      const signedPnl = `${realizedPnL >= 0 ? "+" : "-"}$${Math.abs(realizedPnL).toFixed(2)}`;
      const resetBalance = Number((previous.currentBalance + realizedPnL).toFixed(2));

      return syncRuntimeWithMarket({
        runtime: {
          ...previous,
          simulationStartedAt: null,
          startingBalance: resetBalance,
          sessionStake: settings.tradeAmount,
          currentBalance: resetBalance,
          equity: resetBalance,
          state: "stopped",
          nextTradeAt: null,
          pausedRemainingMs: null,
          realizedProfitLoss: Number(
            (previous.realizedProfitLoss + realizedPnL).toFixed(2)
          ),
          sessionsCompleted: previous.sessionsCompleted + 1,
          wins: previous.wins + (realizedPnL > 0 ? 1 : 0),
          losses: previous.losses + (realizedPnL < 0 ? 1 : 0),
          winRate:
            previous.sessionsCompleted + 1 > 0
              ? ((previous.wins + (realizedPnL > 0 ? 1 : 0)) /
                  (previous.sessionsCompleted + 1)) *
                100
              : 0,
          totalProfitLoss: Number(
            (previous.realizedProfitLoss + realizedPnL).toFixed(2)
          ),
          roiPercent: Number(
            ((((previous.realizedProfitLoss + realizedPnL).toFixed(2) - 0) /
              Math.max(effectiveSettings.tradeAmount, 1)) *
              100).toFixed(2)
          ),
          lastClosedTradePnl: realizedPnL,
          lastTradeStake: previous.sessionStake || effectiveSettings.tradeAmount,
          lastTradeValue: (previous.sessionStake || effectiveSettings.tradeAmount) + Number(
            (previous.realizedProfitLoss + realizedPnL).toFixed(2)
          ),
          latestSummary: `${selectedMarket.label} manual stop | ${signedPnl} execution event`,
          activeTradeStatus: `Stopped after manual close ${signedPnl}`,
          openPosition: null,
          activeTradingAmount: 0,
          markers: [
            ...previous.markers.slice(-59),
            {
              time: latestCandle?.time || Math.floor(Date.now() / 1000),
              position: "aboveBar",
              color: realizedPnL >= 0 ? "#22c55e" : "#ef4444",
              shape: "arrowDown",
              text: `STOP ${realizedPnL >= 0 ? "+" : "-"}$${Math.abs(realizedPnL).toFixed(1)}`,
            },
          ],
          logs: [
            ...previous.logs,
            createExecutionLog(
              `Bot stopped. Active position manually closed at ${signedPnl}. Values preserved until restart or refresh.`,
              realizedPnL >= 0 ? "success" : "error"
            ),
          ].slice(-140),
        },
        marketPrice,
      });
    });

    const defaultSettings = getDefaultSettings();
    setSettings(defaultSettings);
    setTradeAmountInput("");
    setSavedConfiguration(null);
    setIsConfigurationSaved(false);
    addNotification({
      type: "info",
      title: "Trade Live",
      message: "Bot stopped. Configuration reset to defaults for the next session.",
    });
  };

  const stats = [
    {
      label: "Portfolio Balance",
      value: formatMoney(
        runtime.state === "stopped" ? sharedPortfolioBalance : runtime.currentBalance
      ),
      accent: "text-cyan-300",
    },
    { label: "Trade Stake", value: formatMoney(tradeStake), accent: "text-white" },
    {
      label: "Stake + P/L",
      value: formatMoney(stakePlusPnlValue),
      accent: stakePlusPnlValue >= tradeStake ? "text-emerald-300" : "text-rose-300",
    },
    { label: "Sessions Completed", value: String(runtime.sessionsCompleted), accent: "text-emerald-300" },
    { label: "Profit and Loss", value: formatMoney(runtime.totalProfitLoss), accent: runtime.totalProfitLoss >= 0 ? "text-emerald-300" : "text-rose-300" },
    { label: "Strategy ROI", value: formatPct(runtime.roiPercent), accent: runtime.roiPercent >= 0 ? "text-emerald-300" : "text-rose-300" },
    { label: "Last Closed Trade", value: lastClosedTradeLabel, accent: lastClosedTradeTone },
    { label: "Win Rate", value: `${runtime.winRate.toFixed(1)}%`, accent: "text-violet-300" },
    { label: "Active Trade Status", value: runtime.activeTradeStatus, accent: "text-slate-100" },
    {
      label: "Current Bot Mode",
      value: settings.botSelection
        ? botProfile.label
        : "Awaiting setup",
      accent: "text-sky-300",
    },
  ];

  const executionRail = (() => {
    if (!configurationReady) {
      return {
        label: "CONFIGURATION REQUIRED",
        tone: "text-slate-200",
        border: "border-slate-700/80",
        background:
          "bg-[linear-gradient(135deg,rgba(15,23,42,0.92),rgba(30,41,59,0.8))]",
        glow: "from-slate-500/25 via-slate-300/10 to-transparent",
        pulse: "bg-slate-400/70",
      };
    }

    if (runtime.state !== "paused" && runtime.state !== "stopped") {
      return {
        label: "RUNNING",
        tone: "text-emerald-200",
        border: "border-emerald-400/40",
        background:
          "bg-[linear-gradient(135deg,rgba(6,95,70,0.92),rgba(5,150,105,0.55))]",
        glow: "from-emerald-300/35 via-emerald-200/15 to-transparent",
        pulse: "bg-emerald-300",
      };
    }

    if (runtime.state === "paused") {
      return {
        label: "PAUSED",
        tone: "text-amber-100",
        border: "border-amber-400/40",
        background:
          "bg-[linear-gradient(135deg,rgba(120,53,15,0.92),rgba(217,119,6,0.45))]",
        glow: "from-amber-300/35 via-amber-100/15 to-transparent",
        pulse: "bg-amber-300",
      };
    }

    return {
      label: "READY TO START",
      tone: "text-rose-100",
      border: "border-rose-400/40",
      background:
        "bg-[linear-gradient(135deg,rgba(127,29,29,0.95),rgba(225,29,72,0.5))]",
      glow: "from-rose-300/35 via-rose-100/10 to-transparent",
      pulse: "bg-rose-300",
    };
  })();
  const isExecutionRunning =
    configurationReady &&
    runtime.state !== "paused" &&
    runtime.state !== "stopped";
  const isBotPaused = runtime.state === "paused";
  const canTogglePause = runtime.state !== "stopped";

  return (
    <DashboardLayout>
      <div className="space-y-5 p-4 text-slate-100 sm:space-y-6 sm:p-6 lg:p-8">
        <Card className="overflow-hidden border-slate-800 bg-[radial-gradient(circle_at_top_left,_rgba(8,145,178,0.22),_transparent_28%),linear-gradient(135deg,#020617,#07111f_48%,#0f172a)] p-5 text-slate-100 shadow-2xl shadow-cyan-950/20 sm:p-7">
          <div className="flex flex-col gap-6 xl:flex-row xl:items-end xl:justify-between">
            <div>
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.24em] text-cyan-300">
                  <RadioTower className="h-3.5 w-3.5 animate-pulse" />
                  Smart AI Engine
                </span>
                <span className={`inline-flex items-center gap-2 rounded-full border border-slate-700 bg-slate-900/70 px-3 py-1 text-xs font-medium ${stateMeta.tone}`}>
                  <span className={`h-2.5 w-2.5 rounded-full ${stateMeta.dot} animate-pulse`} />
                  {stateMeta.label}
                </span>
              </div>
              <h1 className="text-3xl font-semibold tracking-tight">
                Trade{" "}
                <motion.span
                  className="inline-block text-rose-300"
                  animate={{
                    textShadow: [
                      "0 0 0 rgba(244,63,94,0)",
                      "0 0 12px rgba(244,63,94,0.65)",
                      "0 0 0 rgba(244,63,94,0)",
                    ],
                    opacity: [0.82, 1, 0.82],
                  }}
                  transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
                >
                  Live
                </motion.span>
              </h1>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-3xl border border-slate-800 bg-slate-950/70 px-4 py-4 text-sm">
                <div className="text-slate-500">Latest AI Execution Summary</div>
                <div className="mt-2 font-medium text-slate-100">{runtime.latestSummary}</div>
              </div>
              <div className="rounded-3xl border border-slate-800 bg-slate-950/70 px-4 py-4 text-sm">
                <div className="text-slate-500">Active Pair</div>
                <div className="mt-2 text-xl font-semibold text-cyan-300">{selectedMarket.label}</div>
                <div className="mt-1 text-xs text-slate-500">AI execution engine connected to live market feed</div>
              </div>
            </div>
          </div>
        </Card>

        <div className="grid gap-4 sm:gap-6 xl:grid-cols-[0.92fr_1.08fr] xl:items-start">
          <Card className="border-slate-800 bg-slate-950/90 text-slate-100 xl:col-start-1 xl:row-start-1">
              <div className="mb-5 flex items-center gap-2 text-sm font-medium text-slate-300">
                <ShieldCheck className="h-4 w-4 text-cyan-300" />
                AI Strategy Configuration
              </div>
              <div className="mb-4 rounded-2xl border border-cyan-400/15 bg-cyan-500/[0.04] p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-[0.28em] text-cyan-300">
                      Configuration Progress
                    </div>
                    <div className="mt-1 text-xs text-slate-400">
                      Build your next AI strategy setup before arming execution.
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-semibold text-white">{configurationCompletion}%</div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500">
                      Complete
                    </div>
                  </div>
                </div>
                <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-900">
                  <motion.div
                    className="h-full rounded-full bg-[linear-gradient(90deg,#06b6d4,#22c55e)]"
                    animate={{ width: `${configurationCompletion}%` }}
                    transition={{ duration: 0.35, ease: "easeOut" }}
                  />
                </div>
                <div className="mt-4 grid gap-3 sm:grid-cols-3">
                  {configurationChecklist.map((item) => (
                    <div
                      key={item.label}
                      className={`rounded-2xl border px-3 py-3 transition ${
                        item.complete
                          ? "border-emerald-400/20 bg-emerald-500/[0.06]"
                          : "border-slate-800 bg-slate-900/70"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[11px] uppercase tracking-[0.22em] text-slate-500">
                          {item.label}
                        </span>
                        <span
                          className={`h-2.5 w-2.5 rounded-full ${
                            item.complete ? "bg-emerald-300 shadow-[0_0_12px_rgba(110,231,183,0.6)]" : "bg-slate-600"
                          }`}
                        />
                      </div>
                      <div className="mt-2 text-sm font-medium text-slate-100">{item.value}</div>
                    </div>
                  ))}
                </div>
              </div>
              <motion.div
                className={`relative mb-4 overflow-hidden rounded-2xl border px-4 py-3 ${executionRail.border} ${executionRail.background}`}
                initial={{ opacity: 0.88, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, ease: "easeOut" }}
              >
                <motion.div
                  className={`pointer-events-none absolute inset-y-0 left-[-30%] w-1/2 bg-gradient-to-r ${executionRail.glow} blur-xl`}
                  animate={isExecutionRunning ? { x: ["0%", "165%"] } : { x: "42%" }}
                  transition={
                    isExecutionRunning
                      ? { duration: 2.4, repeat: Infinity, ease: "linear" }
                      : { duration: 0.25, ease: "easeOut" }
                  }
                />
                <div className="relative flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <span className={`h-2.5 w-2.5 rounded-full ${executionRail.pulse} shadow-[0_0_14px_currentColor]`} />
                    <span className={`text-[11px] font-semibold uppercase tracking-[0.34em] ${executionRail.tone}`}>
                      {executionRail.label}
                    </span>
                  </div>
                  <motion.div
                    className="h-1.5 w-20 rounded-full bg-white/10"
                    initial={false}
                  >
                    <motion.div
                      className={`h-full rounded-full ${executionRail.pulse}`}
                      animate={
                        isExecutionRunning
                          ? { width: ["24%", "100%", "38%"] }
                          : { width: "38%" }
                      }
                      transition={
                        isExecutionRunning
                          ? { duration: 1.6, repeat: Infinity, ease: "easeInOut" }
                          : { duration: 0.25, ease: "easeOut" }
                      }
                    />
                  </motion.div>
                </div>
              </motion.div>
              <div
                className={`mb-4 rounded-2xl border px-4 py-3 text-sm ${
                  configurationReady
                    ? "border-emerald-400/20 bg-emerald-500/5 text-emerald-200"
                    : "border-slate-800 bg-slate-900/70 text-slate-300"
                }`}
              >
                {configurationReady
                  ? "Configuration saved successfully"
                  : "Select the bot type, trading pair, and trade amount, then save your configuration before starting."}
              </div>
              <div className="grid gap-4 xl:grid-cols-[1.15fr_1fr]">
                <div className="grid gap-2">
                  <span className="text-xs uppercase tracking-[0.18em] text-slate-500">Bot Selection</span>
                  <div className="grid grid-cols-2 gap-3">
                    {Object.values(BOT_PROFILES).map((profile) => (
                      <button
                        key={profile.id}
                        onClick={() =>
                          setSettings((previous) => ({ ...previous, botSelection: profile.id }))
                        }
                        className={`rounded-2xl border px-4 py-4 text-left transition ${
                          settings.botSelection === profile.id
                            ? "border-cyan-400 bg-cyan-500/10"
                            : "border-slate-800 bg-slate-900/70 hover:border-slate-600"
                        }`}
                      >
                        <div className="font-medium text-white">{profile.label}</div>
                        <div className="mt-1 text-xs text-slate-400">{profile.description}</div>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid gap-4">
                  <label className="grid gap-2">
                    <span className="text-xs uppercase tracking-[0.18em] text-slate-500">Trading Pair</span>
                    <select
                      value={settings.pair}
                      onChange={(event) =>
                        setSettings((previous) => ({ ...previous, pair: event.target.value }))
                      }
                      className="rounded-2xl border border-slate-700 bg-slate-950 px-3 py-3 text-sm text-slate-100 outline-none transition focus:border-cyan-400"
                    >
                      <option value="" disabled>
                        Select trading pair
                      </option>
                      {MARKET_OPTIONS.map((market) => (
                        <option key={market.symbol} value={market.symbol}>
                          {market.label}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="grid gap-2">
                    <span className="text-xs uppercase tracking-[0.18em] text-slate-500">Trade Amount</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={tradeAmountInput}
                      onChange={(event) => {
                        const sanitizedValue = event.target.value.replace(/[^0-9.]/g, "");
                        setTradeAmountInput(sanitizedValue);

                        if (sanitizedValue === "") {
                          return;
                        }

                        const nextValue = Number(sanitizedValue);
                        if (Number.isFinite(nextValue)) {
                          setSettings((previous) => ({
                            ...previous,
                            tradeAmount: nextValue,
                          }));
                        }
                      }}
                      onBlur={() => {
                        if (tradeAmountInput === "") {
                          setTradeAmountInput(
                            Number.isFinite(settings.tradeAmount)
                              ? String(settings.tradeAmount)
                              : ""
                          );
                        }
                      }}
                      placeholder="Enter investment amount"
                      className="rounded-2xl border border-slate-700 bg-slate-950 px-3 py-3 text-sm text-slate-100 outline-none transition focus:border-cyan-400"
                    />
                  </label>
                </div>
              </div>

              <div className="mt-4 grid gap-2">
                <span className="text-xs uppercase tracking-[0.18em] text-slate-500">Configuration</span>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={saveConfiguration}
                    className="inline-flex items-center gap-2 rounded-2xl border border-cyan-400/30 bg-cyan-500/10 px-4 py-3 font-medium text-cyan-200 transition hover:bg-cyan-500/20"
                  >
                    <ShieldCheck className="h-4 w-4" />
                    Save Configuration
                  </button>
                </div>
              </div>

              <div className="mt-4 grid gap-2">
                <span className="text-xs uppercase tracking-[0.18em] text-slate-500">Execution</span>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={startBot}
                    disabled={!configurationReady}
                    className={`inline-flex items-center gap-2 rounded-2xl px-4 py-3 font-medium transition ${
                      configurationReady
                        ? "bg-emerald-500 text-slate-950 hover:bg-emerald-400"
                        : "cursor-not-allowed bg-slate-800 text-slate-500"
                    }`}
                  >
                    <PlayCircle className="h-4 w-4" />
                    Start Bot
                  </button>
                  <button
                    onClick={pauseBot}
                    disabled={!canTogglePause}
                    className={`inline-flex items-center gap-2 rounded-2xl border px-4 py-3 font-medium transition ${
                      canTogglePause
                        ? "border-amber-400/30 bg-amber-500/10 text-amber-200 hover:bg-amber-500/20"
                        : "cursor-not-allowed border-slate-800 bg-slate-900 text-slate-500"
                    }`}
                  >
                    <AnimatePresence mode="wait" initial={false}>
                      <motion.span
                        key={isBotPaused ? "resume" : "pause"}
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        transition={{ duration: 0.18, ease: "easeOut" }}
                        className="inline-flex items-center gap-2"
                      >
                        {isBotPaused ? (
                          <PlayCircle className="h-4 w-4" />
                        ) : (
                          <PauseCircle className="h-4 w-4" />
                        )}
                        {isBotPaused ? "Resume Bot" : "Pause Bot"}
                      </motion.span>
                    </AnimatePresence>
                  </button>
                  <button
                    onClick={stopBot}
                    className="inline-flex items-center gap-2 rounded-2xl border border-rose-400/30 bg-rose-500/10 px-4 py-3 font-medium text-rose-200 transition hover:bg-rose-500/20"
                  >
                    <Power className="h-4 w-4" />
                    Stop Bot
                  </button>
                </div>
              </div>
          </Card>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-3 xl:col-start-2 xl:row-start-1">
            {stats.map((stat) => (
              <Card
                key={stat.label}
                className="border-slate-800 bg-slate-950/90 p-4 text-slate-100 transition duration-300 hover:border-cyan-500/30"
              >
                <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500">{stat.label}</div>
                <div className={`mt-2 text-lg font-semibold leading-tight sm:text-xl ${stat.accent}`}>{stat.value}</div>
              </Card>
            ))}
          </div>

          <Card className="border-slate-800 bg-slate-950/90 text-slate-100 xl:col-start-1 xl:row-start-2">
              <div className="mb-4 flex items-center gap-2 text-sm font-medium text-slate-300">
                <Bot className="h-4 w-4 text-cyan-300" />
                AI EXECUTION LOGS
              </div>
              <div
                ref={logsRef}
                className="h-[720px] overflow-y-auto rounded-3xl border border-slate-800 bg-black/60 p-4 font-mono text-xs leading-6"
              >
                <AnimatePresence initial={false}>
                  {runtime.logs.map((log) => (
                    <motion.div
                      key={log.id}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      className="flex gap-3 border-b border-slate-900/80 py-1 last:border-0"
                    >
                      <span className="shrink-0 text-slate-500">
                        [{new Date(log.timestamp).toLocaleTimeString("en-US", { hour12: false })}]
                      </span>
                      <span
                        className={
                          log.tone === "success"
                            ? "text-emerald-300"
                            : log.tone === "warn"
                            ? "text-amber-300"
                            : log.tone === "error"
                            ? "text-rose-300"
                            : "text-cyan-100"
                        }
                      >
                        {log.message}
                      </span>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
          </Card>

          <Card className="border-slate-800 bg-slate-950/90 p-5 text-slate-100 xl:col-start-2 xl:row-start-2">
            <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <div className="text-sm font-medium text-slate-300">{selectedMarket.label}</div>
                <div className="mt-1 text-3xl font-semibold text-white">{formatMoney(marketPrice)}</div>
                <div className="mt-2 text-sm text-slate-500">
                  RSI {runtime.lastRsi.toFixed(2)} | AI execution confidence {runtime.lastConfidence.toFixed(1)}%
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <div className="rounded-2xl border border-slate-800 bg-slate-900/80 px-3 py-2">
                  <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Bot</div>
                  <div className="mt-2 text-sm font-semibold text-white">{botProfile.label}</div>
                </div>
                <div className="rounded-2xl border border-slate-800 bg-slate-900/80 px-3 py-2">
                  <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Last Closed</div>
                  <div className={`mt-2 text-sm font-semibold ${lastClosedTradeTone}`}>{lastClosedTradeLabel}</div>
                </div>
                <div className="rounded-2xl border border-slate-800 bg-slate-900/80 px-3 py-2">
                  <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Trade Attempts</div>
                  <div className="mt-2 text-sm font-semibold text-cyan-300">{runtime.tradeAttempts}</div>
                </div>
                <div className="rounded-2xl border border-slate-800 bg-slate-900/80 px-3 py-2">
                  <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Execution Position</div>
                  <div className="mt-2 text-sm font-semibold text-slate-100">
                    {runtime.openPosition ? formatMoney(runtime.openPosition.averageEntry) : "None"}
                  </div>
                </div>
              </div>
            </div>
            <div className="grid gap-3">
              <div className="rounded-2xl border border-slate-800 bg-slate-900/80 px-4 py-4">
                <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Live Price Feed</div>
                <div className="mt-2 text-xl font-semibold text-cyan-300">{formatMoney(marketPrice)}</div>
              </div>
            </div>
          </Card>

          <div className="grid gap-4 sm:gap-6 lg:grid-cols-2 lg:items-stretch xl:col-start-2 xl:row-start-3">
            <Card className="h-full border-slate-800 bg-slate-950/90 p-5 text-slate-100">
              <div className="mb-4 flex items-center gap-2 text-sm font-medium text-slate-300">
                <Activity className="h-4 w-4 text-emerald-300" />
                Live Bot Telemetry
              </div>
              <div className="space-y-3 text-sm">
                <div className="rounded-2xl border border-slate-800 bg-slate-900/80 px-4 py-3">
                  <div className="text-slate-400">Current State</div>
                  <div className={`mt-1 font-semibold ${stateMeta.tone}`}>{stateMeta.label}</div>
                </div>
                <div className="rounded-2xl border border-slate-800 bg-slate-900/80 px-4 py-3">
                  <div className="text-slate-400">Pair</div>
                  <div className="mt-1 font-semibold text-white">{selectedMarket.label}</div>
                </div>
                <div className="rounded-2xl border border-slate-800 bg-slate-900/80 px-4 py-3">
                  <div className="text-slate-400">Risk Logic</div>
                  <div className="mt-1 font-semibold text-white">
                    Entry RSI {botProfile.entryRsi} | TP {formatPct(botProfile.takeProfitPercent)} | DCA {botProfile.maxDcaOrders}
                  </div>
                </div>
                <div className="rounded-2xl border border-slate-800 bg-slate-900/80 px-4 py-3">
                  <div className="text-slate-400">Execution Bus</div>
                  <div className="mt-1 font-semibold text-cyan-300">
                    {transportStatus === "connected" ? "Streaming market data into AI execution engine" : "Awaiting stream"}
                  </div>
                </div>
              </div>
            </Card>

            <Card className="h-full border-slate-800 bg-slate-950/90 p-5 text-slate-100">
              <div className="mb-4 flex items-center gap-2 text-sm font-medium text-slate-300">
                <TrendingUp className="h-4 w-4 text-cyan-300" />
                Live Execution Metrics
              </div>
              <div className="grid gap-3">
                <div className="rounded-2xl border border-slate-800 bg-slate-900/80 px-4 py-3">
                  <div className="text-slate-400">Trade Confidence</div>
                  <div className="mt-1 text-xl font-semibold text-cyan-300">
                    {runtime.lastConfidence.toFixed(1)}%
                  </div>
                </div>
                <div className="rounded-2xl border border-slate-800 bg-slate-900/80 px-4 py-3">
                  <div className="text-slate-400">RSI Snapshot</div>
                  <div className="mt-1 text-xl font-semibold text-white">
                    {runtime.lastRsi.toFixed(2)}
                  </div>
                </div>
                <div className="rounded-2xl border border-slate-800 bg-slate-900/80 px-4 py-3">
                  <div className="text-slate-400">Backend Pulse</div>
                  <div className="mt-1 text-xl font-semibold text-emerald-300">
                    {runtime.backendPulse}
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default TradeLive;
